import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "helpers/jwt-token-access/customBaseQuery";

const convertToFormData = (
  obj: Record<string, any> | undefined | null,
  parentKey: string = "",
): FormData => {
  const formData = new FormData();

  const appendData = (key: string, value: any) => {
    if (value instanceof File) {
      formData.append(key, value);
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item instanceof File) {
          formData.append(`${key}[${index}]`, item);
        } else {
          formData.append(`${key}[${index}]`, item);
        }
      });
    } else if (value && typeof value === "object") {
      if (value instanceof Date) {
        formData.append(key, value.toISOString());
      } else {
        Object.keys(value).forEach((subKey) => {
          appendData(`${key}[${subKey}]`, value[subKey]);
        });
      }
    } else {
      formData.append(key, value);
    }
  };

  if (obj) {
    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      appendData(parentKey ? `${parentKey}[${key}]` : key, value);
    });
  }

  return formData;
};

export const safetyAppSlice = createApi({
  reducerPath: "safetyApp",
  baseQuery: customBaseQuery,
  tagTypes: [
    "GetHomeWorkPermit",
    "GetHomeworkPermitByNumber",
    "GetHotworkPermit",
    "GetHotworkPermitByNumber",
    "GetGeneralworkPermit",
    "GetGeneralworkPermitByNumber",
    "GetWorkAtHeightPermit",
    "GetWorkAtHeightPermitByNumber",
    "GetExcavationPermitByNumber",
    "GetExcavationPermit",
    "GetLiftingWorkPermit",
    "GetLiftingWorkPermitByNumber",
    "GetConfinedSpacePermit",
    "GetConfinedSpacePermitByNumber",
    "GetToolboxMeeting",
    "ToolboxMeeting",
    "GetPillingPermit",
    "GetPillingPermitByNumber",
    "GetPipeJackingPermitByNumber",
    "GetPipeJackingPermit",
  ],
  endpoints: (builder) => ({
    //---HomeWork Permit EndPoints
    getHomeworkPermitView: builder.query({
      query: () => "/safety_app/api/v1/homework_permit_view/",
      providesTags: ["GetHomeWorkPermit"],
    }),
    getHomeworkPermitByNumber: builder.query({
      query: (hw_permit_no) =>
        `/safety_app/api/v1/homework_permit_view/${hw_permit_no}/`,
      providesTags: (result, error, hw_permit_no) => [
        { type: "GetHomeworkPermitByNumber", id: hw_permit_no },
      ],
    }),
    createHomeworkPermit: builder.mutation({
      query: (newPermitData) => ({
        url: "/safety_app/api/v1/homework_permit_create_update/",
        method: "POST",
        body: newPermitData,
      }),
    }),
    updateHomeworkPermit: builder.mutation({
      query: ({ hw_permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/homework_permit_create_update/${hw_permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
    }),

    // New endpoints for homework permit questionnaire key
    getHomeworkPermitQuestionnaireKeys: builder.query({
      query: () => "/safety_app/api/v1/homework_permit_questionnaire_key/",
    }),
    getHomeworkPermitQuestionnaireKeyById: builder.query({
      query: (id) =>
        `/safety_app/api/v1/homework_permit_questionnaire_key/${id}/`,
    }),
    createHomeworkPermitQuestionnaireKey: builder.mutation({
      query: (newQuestionnaireKeyData) => ({
        url: "/safety_app/api/v1/homework_permit_questionnaire_key/",
        method: "POST",
        body: newQuestionnaireKeyData,
      }),
    }),
    updateHomeworkPermitQuestionnaireKeyByHwPermitNo: builder.mutation({
      query: ({ hw_permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/homework_permit_questionnaire_update/${hw_permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
    }),
    updateHomeworkPermitToolboxMeetingByHwPermitNo: builder.mutation({
      query: ({ hw_permit_no, updatedToolboxMeetingData }) => ({
        url: `/safety_app/api/v1/homework_permit_toolbox_meeting/${hw_permit_no}/`,
        method: "PATCH",
        body: updatedToolboxMeetingData,
      }),
    }),
    generateHomeworkPermitPDF: builder.mutation({
      query: (hw_permit_no) => ({
        url: `/safety_app/api/v1/generate-pdf/${hw_permit_no}`,
        method: "GET",
        responseHandler: async (response: any) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${hw_permit_no}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        },
      }),
    }),
    uploadHomeworkPermitDocument: builder.mutation({
      query: ({ hw_permit_no, documentData }) => ({
        url: `/safety_app/api/v1/homework_permit_document_update/${hw_permit_no}/`,
        method: "PATCH",
        body: documentData,
      }),
    }),
    deleteHomeworkPermitDocument: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/homework_permit_document_delete/${id}/`,
        method: "DELETE",
      }),
    }),

    updateHomeworkWorkAreaAllocation: builder.mutation({
      query: ({ hw_permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/homework_work_area_allocation_update/${hw_permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
    }),
    deleteHomeworkWorkAreaAllocation: builder.mutation({
      query: ({
        sectionId,
        employeeId,
        type,
      }: {
        sectionId: number;
        employeeId: number | null;
        type: "employee" | "section";
      }) => {
        // Conditional URL based on type
        const url =
          type === "section"
            ? `/safety_app/api/v1/homework_work_area_allocation_delete/${sectionId}?type=section`
            : `/safety_app/api/v1/homework_work_area_allocation_delete/${sectionId}?type=employee&employee_id=${employeeId}`;

        return {
          url,
          method: "DELETE",
        };
      },
    }),

    // --- Hotwork Endpoints ---
    getHotworkPermitView: builder.query({
      query: (page) =>
        `/safety_app/api/v1/hotwork_permit_view/?page=${page.page}&page_size=${page.pageSize}&search=${page.search}&report_status=${page.report_status}&start_date=${page.start_date}&end_date=${page.end_date}&project=${page.project}`,
      providesTags: ["GetHotworkPermit"],
    }),
    getHotworkPermitByNumber: builder.query({
      query: (hotwork_permit_no) =>
        `/safety_app/api/v1/hotwork_permit_view/${hotwork_permit_no}/`,
      providesTags: (result, error, hotwork_permit_no) => [
        { type: "GetHotworkPermitByNumber", id: hotwork_permit_no },
      ],
    }),
    createHotworkPermit: builder.mutation({
      query: (newPermitData) => ({
        url: "/safety_app/api/v1/hotwork_permit_create_update/",
        method: "POST",
        body: newPermitData,
      }),
      invalidatesTags: ["GetHotworkPermit"],
    }),
    deleteHotworkPermit: builder.mutation({
      query: ({ permitNo, permitData }) => ({
        url: `/safety_app/api/v1/hotwork_permit_create_update/${permitNo}/?user_comments=${permitData.user_comments}&latitude=${permitData.latitude}&longitude=${permitData.longitude}`,
        method: "DELETE",
      }),
    }),
    updateHotworkPermit: builder.mutation({
      query: ({ hw_permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/hotwork_permit_create_update/${hw_permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
      invalidatesTags: (result, error, { hw_permit_no }) => [
        { type: "GetHotworkPermitByNumber", id: hw_permit_no },
        { type: "GetHotworkPermit" },
      ],
    }),
    uploadHotworkPermitDocument: builder.mutation({
      query: ({ hw_permit_no, documentData }) => ({
        url: `/safety_app/api/v1/hotwork_permit_document_update/${hw_permit_no}/`,
        method: "PATCH",
        body: documentData,
      }),
    }),
    deleteHotworkPermitDocument: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/hotwork_permit_document_delete/${id}/`,
        method: "DELETE",
      }),
    }),
    updateHotworkWorkAreaAllocation: builder.mutation({
      query: ({ hw_permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/hotwork_work_area_allocation/${hw_permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
    }),
    deleteHotworkWorkAreaAllocation: builder.mutation({
      query: ({
        sectionId,
        employeeId,
        type,
      }: {
        sectionId: number;
        employeeId: number | null;
        type: "employee" | "section";
      }) => {
        return {
          url: `/safety_app/api/v1/hotwork_area_allocation_delete/${sectionId}/`, // Updated endpoint
          method: "PATCH",
          body: {
            type, // Pass type in the body
            ...(type === "employee" && { employee_id: employeeId }), // Conditionally include employee_id
          },
        };
      },
    }),
    getHotworkQuestionnaireKeys: builder.query({
      query: () => "/safety_app/api/v1/hotwork_permit_questionnaire_key/",
    }),
    getHotworkQuestionnaireKeyById: builder.query({
      query: (id) =>
        `/safety_app/api/v1/hotwork_permit_questionnaire_key/${id}/`,
    }),
    createHotworkQuestionnaireKey: builder.mutation({
      query: (newQuestionnaireKeyData) => ({
        url: "/safety_app/api/v1/hotwork_permit_questionnaire_key/",
        method: "POST",
        body: newQuestionnaireKeyData,
      }),
    }),
    updateHotworkQuestionnaireKeyById: builder.mutation({
      query: ({ id, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/hotwork_permit_questionnaire_key/${id}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
    }),
    updateHotworkQuestionnaireByPermitNo: builder.mutation({
      query: ({ hw_permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/hotwork_permit_questionnaire_update/${hw_permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { hw_permit_no }) => [
      //   { type: "GetHotworkPermitByNumber", id: hw_permit_no },
      // ],
    }),

    //GeneralWork Permit EndPoints
    getGeneralworkPermitView: builder.query({
      query: (page) =>
        `/safety_app/api/v1/generalwork_permit_view/?page=${page.page}&page_size=${page.pageSize}&search=${page.search}&report_status=${page.report_status}&start_date=${page.start_date}&end_date=${page.end_date}&project=${page.project}`,
      providesTags: ["GetGeneralworkPermit"],
    }),
    getGeneralworkPermitByNumber: builder.query({
      query: (generalwork_permit_no) =>
        `/safety_app/api/v1/generalwork_permit_view/${generalwork_permit_no}/`,
      providesTags: (result, error, generalwork_permit_no) => [
        { type: "GetGeneralworkPermitByNumber", id: generalwork_permit_no },
      ],
    }),
    createGeneralworkPermit: builder.mutation({
      query: (newPermitData) => ({
        url: "/safety_app/api/v1/generalwork_permit_create_update/",
        method: "POST",
        body: newPermitData,
      }),
      invalidatesTags: ["GetGeneralworkPermit"],
    }),
    updateGeneralworkPermit: builder.mutation({
      query: ({ gw_permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/generalwork_permit_create_update/${gw_permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
      invalidatesTags: (result, error, { gw_permit_no }) => [
        { type: "GetGeneralworkPermitByNumber", id: gw_permit_no },
        { type: "GetGeneralworkPermit" },
      ],
    }),
    deleteGeneralworkPermit: builder.mutation({
      query: ({ permitNo, permitData }) => ({
        url: `/safety_app/api/v1/generalwork_permit_create_update/${permitNo}/?user_comments=${permitData.user_comments}&latitude=${permitData.latitude}&longitude=${permitData.longitude}`,
        method: "DELETE",
      }),
    }),
    uploadGeneralworkPermitDocument: builder.mutation({
      query: ({ gw_permit_no, documentData }) => ({
        url: `/safety_app/api/v1/generalwork_permit_document_update/${gw_permit_no}/`,
        method: "PATCH",
        body: documentData,
      }),
    }),
    deleteGeneralworkPermitDocument: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/generalwork_permit_document_delete/${id}/`,
        method: "DELETE",
      }),
    }),
    updateGeneralworkWorkAreaAllocation: builder.mutation({
      query: ({ gw_permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/generalwork_work_area_allocation/${gw_permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
    }),
    deleteGeneralworkWorkAreaAllocation: builder.mutation({
      query: ({
        sectionId,
        employeeId,
        type,
      }: {
        sectionId: number;
        employeeId: number | null;
        type: "employee" | "section";
      }) => {
        return {
          url: `/safety_app/api/v1/generalwork_work_area_allocation_delete/${sectionId}/`, // Updated endpoint
          method: "PATCH",
          body: {
            type, // Pass type in the body
            ...(type === "employee" && { employee_id: employeeId }), // Conditionally include employee_id
          },
        };
      },
    }),
    getGeneralworkQuestionnaireKeys: builder.query({
      query: () => "/safety_app/api/v1/generalwork_permit_questionnaire_key/",
    }),
    getGeneralworkQuestionnaireKeyById: builder.query({
      query: (id) =>
        `/safety_app/api/v1/generalwork_permit_questionnaire_key/${id}/`,
    }),
    createGeneralworkQuestionnaireKey: builder.mutation({
      query: (newQuestionnaireKeyData) => ({
        url: "/safety_app/api/v1/generalwork_permit_questionnaire_key/",
        method: "POST",
        body: newQuestionnaireKeyData,
      }),
    }),
    updateGeneralworkQuestionnaireByPermitNo: builder.mutation({
      query: ({ gw_permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/generalwork_permit_questionnaire_update/${gw_permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { gw_permit_no }) => [
      //   { type: "GetGeneralworkPermitByNumber", id: gw_permit_no },
      // ],
    }),

    //WorkAtHeight End Points

    getWorkAtHeightPermitView: builder.query({
      query: (page) =>
        `/safety_app/api/v1/workatheight_permit_view/?page=${page.page}&page_size=${page.pageSize}&search=${page.search}&report_status=${page.report_status}&start_date=${page.start_date}&end_date=${page.end_date}&project=${page.project}`,
      providesTags: ["GetWorkAtHeightPermit"],
    }),
    getWorkAtHeightPermitByNumber: builder.query({
      query: (wh_permit_no) =>
        `/safety_app/api/v1/workatheight_permit_view/${wh_permit_no}/`,
      providesTags: (result, error, wh_permit_no) => [
        { type: "GetWorkAtHeightPermitByNumber", id: wh_permit_no },
      ],
    }),
    createWorkAtHeightPermit: builder.mutation({
      query: (newPermitData) => ({
        url: "/safety_app/api/v1/workatheight_permit_create_update/",
        method: "POST",
        body: newPermitData,
      }),
      invalidatesTags: ["GetWorkAtHeightPermit"],
    }),
    deleteWorkAtHeightPermit: builder.mutation({
      query: ({ permitNo, permitData }) => ({
        url: `/safety_app/api/v1/workatheight_permit_create_update/${permitNo}/?user_comments=${permitData.user_comments}&latitude=${permitData.latitude}&longitude=${permitData.longitude}`,
        method: "DELETE",
      }),
    }),
    updateWorkAtHeightPermit: builder.mutation({
      query: ({ wh_permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/workatheight_permit_create_update/${wh_permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
      invalidatesTags: (result, error, { wh_permit_no }) => [
        { type: "GetWorkAtHeightPermitByNumber", id: wh_permit_no },
        { type: "GetWorkAtHeightPermit" },
      ],
    }),
    uploadWorkAtHeightPermitDocument: builder.mutation({
      query: ({ wh_permit_no, documentData }) => ({
        url: `/safety_app/api/v1/workatheight_permit_document_update/${wh_permit_no}/`,
        method: "PATCH",
        body: documentData,
      }),
    }),
    deleteWorkAtHeightPermitDocument: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/workatheight_permit_document_delete/${id}/`,
        method: "DELETE",
      }),
    }),
    updateWorkAtHeightWorkAreaAllocation: builder.mutation({
      query: ({ wh_permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/workatheight_area_allocation/${wh_permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
    }),
    deleteWorkAtHeightWorkAreaAllocation: builder.mutation({
      query: ({
        sectionId,
        employeeId,
        type,
      }: {
        sectionId: number;
        employeeId: number | null;
        type: "employee" | "section";
      }) => {
        return {
          url: `/safety_app/api/v1/workatheight_area_allocation_delete/${sectionId}/`, // Updated endpoint
          method: "PATCH",
          body: {
            type, // Pass type in the body
            ...(type === "employee" && { employee_id: employeeId }), // Conditionally include employee_id
          },
        };
      },
    }),
    getWorkAtHeightQuestionnaireKeys: builder.query({
      query: () => "/safety_app/api/v1/workatheight_permit_questionnaire_key/",
    }),
    getWorkAtHeightQuestionnaireKeyById: builder.query({
      query: (id) =>
        `/safety_app/api/v1/workatheight_permit_questionnaire_key/${id}/`,
    }),
    createWorkAtHeightQuestionnaireKey: builder.mutation({
      query: (newQuestionnaireKeyData) => ({
        url: "/safety_app/api/v1/workatheight_permit_questionnaire_key/",
        method: "POST",
        body: newQuestionnaireKeyData,
      }),
    }),
    updateWorkAtHeightQuestionnaireByPermitNo: builder.mutation({
      query: ({ wh_permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/workatheight_permit_questionnaire_update/${wh_permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { wh_permit_no }) => [
      //   { type: "GetWorkAtHeightPermitByNumber", id: wh_permit_no },
      // ],
    }),

    // Excavation Permit Endpoints
    getExcavationPermitView: builder.query({
      query: (page) =>
        `/safety_app/api/v1/excavation_permit_view/?page=${page.page}&page_size=${page.pageSize}&search=${page.search}&report_status=${page.report_status}&start_date=${page.start_date}&end_date=${page.end_date}&project=${page.project}`,
      providesTags: ["GetExcavationPermit"],
    }),
    getExcavationPermitByNumber: builder.query({
      query: (excavation_permit_no) =>
        `/safety_app/api/v1/excavation_permit_view/${excavation_permit_no}/`,
      providesTags: (result, error, excavation_permit_no) => [
        { type: "GetExcavationPermitByNumber", id: excavation_permit_no },
      ],
    }),
    createExcavationPermit: builder.mutation({
      query: (newPermitData) => ({
        url: "/safety_app/api/v1/excavation_permit_create_update/",
        method: "POST",
        body: newPermitData,
      }),
      invalidatesTags: ["GetExcavationPermit"],
    }),
    deleteExcavationPermit: builder.mutation({
      query: ({ permitNo, permitData }) => ({
        url: `/safety_app/api/v1/excavation_permit_create_update/${permitNo}/?user_comments=${permitData.user_comments}&latitude=${permitData.latitude}&longitude=${permitData.longitude}`,
        method: "DELETE",
      }),
    }),
    updateExcavationPermit: builder.mutation({
      query: ({ ew_permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/excavation_permit_create_update/${ew_permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
      invalidatesTags: (result, error, { ew_permit_no }) => [
        { type: "GetExcavationPermitByNumber", id: ew_permit_no },
        { type: "GetExcavationPermit" },
      ],
    }),
    uploadExcavationPermitDocument: builder.mutation({
      query: ({ ew_permit_no, documentData }) => ({
        url: `/safety_app/api/v1/excavation_permit_document_update/${ew_permit_no}/`,
        method: "PATCH",
        body: documentData,
      }),
    }),
    deleteExcavationPermitDocument: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/excavation_permit_document_delete/${id}/`,
        method: "DELETE",
      }),
    }),
    updateExcavationWorkAreaAllocation: builder.mutation({
      query: ({ ew_permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/excavation_area_allocation/${ew_permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
    }),
    deleteExcavationWorkAreaAllocation: builder.mutation({
      query: ({
        sectionId,
        employeeId,
        type,
      }: {
        sectionId: number;
        employeeId: number | null;
        type: "employee" | "section";
      }) => {
        return {
          url: `/safety_app/api/v1/excavation_area_allocation_delete/${sectionId}/`, // Updated endpoint
          method: "PATCH",
          body: {
            type, // Pass type in the body
            ...(type === "employee" && { employee_id: employeeId }), // Conditionally include employee_id
          },
        };
      },
    }),
    getExcavationQuestionnaireKeys: builder.query({
      query: () => "/safety_app/api/v1/excavation_permit_questionnaire_key/",
    }),
    getExcavationQuestionnaireKeyById: builder.query({
      query: (id) =>
        `/safety_app/api/v1/excavation_permit_questionnaire_key/${id}/`,
    }),
    createExcavationQuestionnaireKey: builder.mutation({
      query: (newQuestionnaireKeyData) => ({
        url: "/safety_app/api/v1/excavation_permit_questionnaire_key/",
        method: "POST",
        body: newQuestionnaireKeyData,
      }),
    }),
    updateExcavationQuestionnaireByPermitNo: builder.mutation({
      query: ({ ew_permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/excavation_permit_questionnaire_update/${ew_permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { ew_permit_no }) => [
      //   { type: "GetExcavationPermitByNumber", id: ew_permit_no },
      // ],
    }),

    // --- Lifting Work Endpoints ---
    getLiftingWorkPermitView: builder.query({
      query: (page) =>
        `/safety_app/api/v1/lifting_permit_view/?page=${page.page}&page_size=${page.pageSize}&search=${page.search}&report_status=${page.report_status}&start_date=${page.start_date}&end_date=${page.end_date}&project=${page.project}`,
      providesTags: ["GetLiftingWorkPermit"],
    }),
    getLiftingWorkPermitByNumber: builder.query({
      query: (lp_permit_no) =>
        `/safety_app/api/v1/lifting_permit_view/${lp_permit_no}/`,
      providesTags: (result, error, lp_permit_no) => [
        { type: "GetLiftingWorkPermitByNumber", id: lp_permit_no },
      ],
    }),
    createLiftingWorkPermit: builder.mutation({
      query: (newPermitData) => ({
        url: "/safety_app/api/v1/lifting_permit_create_update/",
        method: "POST",
        body: newPermitData,
      }),
      invalidatesTags: ["GetLiftingWorkPermit"],
    }),
    deleteLiftingWorkPermit: builder.mutation({
      query: ({ permitNo, permitData }) => ({
        url: `/safety_app/api/v1/lifting_permit_create_update/${permitNo}/?user_comments=${permitData.user_comments}&latitude=${permitData.latitude}&longitude=${permitData.longitude}`,
        method: "DELETE",
      }),
    }),
    updateLiftingWorkPermit: builder.mutation({
      query: ({ lp_permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/lifting_permit_create_update/${lp_permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
      invalidatesTags: (result, error, { lp_permit_no }) => [
        { type: "GetLiftingWorkPermitByNumber", id: lp_permit_no },
        { type: "GetLiftingWorkPermit" },
      ],
    }),
    uploadLiftingWorkPermitDocument: builder.mutation({
      query: ({ lp_permit_no, documentData }) => ({
        url: `/safety_app/api/v1/lifting_permit_document_update/${lp_permit_no}/`,
        method: "PATCH",
        body: documentData,
      }),
    }),
    deleteLiftingWorkPermitDocument: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/lifting_permit_document_delete/${id}/`,
        method: "DELETE",
      }),
    }),
    updateLiftingWorkAreaAllocation: builder.mutation({
      query: ({ lp_permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/lifting_area_allocation/${lp_permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
    }),
    deleteLiftingWorkAreaAllocation: builder.mutation({
      query: ({
        sectionId,
        employeeId,
        type,
      }: {
        sectionId: number;
        employeeId: number | null;
        type: "employee" | "section";
      }) => {
        return {
          url: `/safety_app/api/v1/lifting_area_allocation_delete/${sectionId}/`, // Updated endpoint
          method: "PATCH",
          body: {
            type, // Pass type in the body
            ...(type === "employee" && { employee_id: employeeId }), // Conditionally include employee_id
          },
        };
      },
    }),
    getLiftingWorkQuestionnaireKeys: builder.query({
      query: () => "/safety_app/api/v1/lifting_permit_questionnaire_key/",
    }),
    getLiftingWorkQuestionnaireKeyById: builder.query({
      query: (id) =>
        `/safety_app/api/v1/lifting_permit_questionnaire_key/${id}/`,
    }),
    createLiftingWorkQuestionnaireKey: builder.mutation({
      query: (newQuestionnaireKeyData) => ({
        url: "/safety_app/api/v1/lifting_permit_questionnaire_key/",
        method: "POST",
        body: newQuestionnaireKeyData,
      }),
    }),
    updateLiftingWorkQuestionnaireByPermitNo: builder.mutation({
      query: ({ lp_permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/lifting_permit_questionnaire_update/${lp_permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { lp_permit_no }) => [
      //   { type: "GetLiftingWorkPermitByNumber", id: lp_permit_no },
      // ],
    }),

    //----Confined Space Permit----//

    getConfinedSpacePermitView: builder.query({
      query: (page) =>
        `/safety_app/api/v1/confined_space_permit_view/?page=${page.page}&page_size=${page.pageSize}&search=${page.search}&report_status=${page.report_status}&start_date=${page.start_date}&end_date=${page.end_date}&project=${page.project}`,
      providesTags: ["GetConfinedSpacePermit"],
    }),
    getConfinedSpacePermitByNumber: builder.query({
      query: (confined_space_permit_no) =>
        `/safety_app/api/v1/confined_space_permit_view/${confined_space_permit_no}/`,
      providesTags: (result, error, confined_space_permit_no) => [
        {
          type: "GetConfinedSpacePermitByNumber",
          id: confined_space_permit_no,
        },
      ],
    }),
    createConfinedSpacePermit: builder.mutation({
      query: (newPermitData) => ({
        url: "/safety_app/api/v1/confined_space_permit_create_update/",
        method: "POST",
        body: newPermitData,
      }),
      invalidatesTags: ["GetConfinedSpacePermit"],
    }),
    deleteConfinedSpacePermit: builder.mutation({
      query: ({ permitNo, permitData }) => ({
        url: `/safety_app/api/v1/confined_space_permit_create_update/${permitNo}/?user_comments=${permitData.user_comments}&latitude=${permitData.latitude}&longitude=${permitData.longitude}`,
        method: "DELETE",
      }),
    }),
    updateConfinedSpacePermit: builder.mutation({
      query: ({ cs_permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/confined_space_permit_create_update/${cs_permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
      invalidatesTags: (result, error, { cs_permit_no }) => [
        { type: "GetConfinedSpacePermitByNumber", id: cs_permit_no },
        { type: "GetConfinedSpacePermit" },
      ],
    }),
    uploadConfinedSpacePermitDocument: builder.mutation({
      query: ({ cs_permit_no, documentData }) => ({
        url: `/safety_app/api/v1/confined_space_permit_document_update/${cs_permit_no}/`,
        method: "PATCH",
        body: documentData,
      }),
    }),
    deleteConfinedSpacePermitDocument: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/confined_space_permit_document_delete/${id}/`,
        method: "DELETE",
      }),
    }),
    updateConfinedSpaceWorkAreaAllocation: builder.mutation({
      query: ({ cs_permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/confined_space_area_allocation/${cs_permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
    }),
    deleteConfinedSpaceWorkAreaAllocation: builder.mutation({
      query: ({
        sectionId,
        employeeId,
        type,
      }: {
        sectionId: number;
        employeeId: number | null;
        type: "employee" | "section";
      }) => {
        return {
          url: `/safety_app/api/v1/confined_space_area_allocation_delete/${sectionId}/`, // Updated endpoint
          method: "PATCH",
          body: {
            type, // Pass type in the body
            ...(type === "employee" && { employee_id: employeeId }), // Conditionally include employee_id
          },
        };
      },
    }),
    getConfinedSpaceQuestionnaireKeys: builder.query({
      query: () =>
        "/safety_app/api/v1/confined_space_permit_questionnaire_key/",
    }),
    getConfinedSpaceQuestionnaireKeyById: builder.query({
      query: (id) =>
        `/safety_app/api/v1/confined_space_permit_questionnaire_key/${id}/`,
    }),
    createConfinedSpaceQuestionnaireKey: builder.mutation({
      query: (newQuestionnaireKeyData) => ({
        url: "/safety_app/api/v1/confined_space_permit_questionnaire_key/",
        method: "POST",
        body: newQuestionnaireKeyData,
      }),
    }),
    updateConfinedSpaceQuestionnaireByPermitNo: builder.mutation({
      query: ({ cs_permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/confined_space_permit_questionnaire_update/${cs_permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { cs_permit_no }) => [
      //   { type: "GetConfinedSpacePermitByNumber", id: cs_permit_no },
      // ],
    }),
    //--ToolBox-Meeting
    getToolboxMeetingView: builder.query({
      query: (page) =>
        `/safety_app/api/v1/toolbox_meeting_view/?page=${page.page}&page_size=${page.pageSize}&search=${page.search}&status=${page.status}&start_date_of_work=${page.start_date_of_work}&end_date_of_work =${page.end_date_of_work}&projects=${page.projects}`,
      providesTags: ["GetToolboxMeeting"],
    }),
    getToolboxMeetingByFilter: builder.query({
      query: ({ project_id, start_date, end_date }) =>
        `/safety_app/api/v1/toolbox_meeting_drop_down/?project=${project_id}&start_date_of_work=${start_date}&end_date_of_work=${end_date}`,
      providesTags: ["GetToolboxMeeting"],
    }),
    getToolboxMeetingById: builder.query({
      query: (toolbox_id) =>
        `/safety_app/api/v1/toolbox_meeting_view/${toolbox_id}/`,
      providesTags: (result, error, toolbox_id) => [
        { type: "ToolboxMeeting", id: toolbox_id },
      ],
    }),
    createToolboxMeeting: builder.mutation({
      query: (newMeetingData) => ({
        url: `/safety_app/api/v1/toolbox_meeting_create_update/`,
        method: "POST",
        body: newMeetingData,
      }),
      invalidatesTags: [
        { type: "ToolboxMeeting" },
        { type: "GetToolboxMeeting" },
      ],
    }),
    updateToolboxMeeting: builder.mutation({
      query: ({ toolbox_id, updatedMeetingData }) => ({
        url: `/safety_app/api/v1/toolbox_meeting_create_update/${toolbox_id}/`,
        method: "PATCH",
        body: updatedMeetingData,
      }),
      invalidatesTags: (result, error, { toolbox_id }) => [
        { type: "ToolboxMeeting", id: toolbox_id },
        { type: "GetToolboxMeeting" },
      ],
    }),
    updateToolboxAttendance: builder.mutation({
      query: ({ toolbox_id, attendanceData }) => ({
        url: `/safety_app/api/v1/toolbox_attendance_update/${toolbox_id}/`,
        method: "PATCH",
        body: attendanceData,
      }),
      invalidatesTags: (result, error, { toolbox_id }) => [
        { type: "ToolboxMeeting", id: toolbox_id },
        { type: "GetToolboxMeeting" },
      ],
    }),
    uploadToolboxDocument: builder.mutation({
      query: ({ toolbox_id, documentData }) => {
        const userData = convertToFormData(documentData);
        return {
          url: `/safety_app/api/v1/toolbox_document_update/${toolbox_id}/`,
          method: "PATCH",
          body: userData,
        };
      },
      invalidatesTags: (result, error, { toolbox_id }) => [
        { type: "ToolboxMeeting", id: toolbox_id },
        { type: "GetToolboxMeeting" },
      ],
    }),
    deleteToolboxDocument: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/toolbox_document_delete/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { toolbox_id }) => [
        { type: "ToolboxMeeting", id: toolbox_id },
        { type: "GetToolboxMeeting" },
      ],
    }),
    linkToolboxMeeting: builder.mutation({
      query: ({ toolbox_id, linkData }) => ({
        url: `/safety_app/api/v1/link_toolbox_meeting/${toolbox_id}/`,
        method: "PATCH",
        body: linkData,
      }),
      invalidatesTags: (result, error, { toolbox_id }) => [
        { type: "ToolboxMeeting", id: toolbox_id },
        { type: "GetToolboxMeeting" },
      ],
    }),
    deleteToolboxAttendance: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/toolbox_attendance_delete/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "ToolboxMeeting" }],
    }),
    updateGeneralWorkQuestionnaireKeyById: builder.mutation({
      query: ({ id, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/generalwork_permit_questionnaire_key/${id}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
    }),
    //end
    updateLiftingWorkQuestionnaireKeyById: builder.mutation({
      query: ({ id, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/lifting_permit_questionnaire_key/${id}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
    }),
    //end
    updateConfinedSpaceQuestionnaireKeyById: builder.mutation({
      query: ({ id, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/confined_space_permit_questionnaire_key/${id}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
    }),
    //end
    updateWorkAtHeightQuestionnaireKeyById: builder.mutation({
      query: ({ id, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/workatheight_permit_questionnaire_key/${id}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
    }),
    //end
    updateExcavationQuestionnaireKeyById: builder.mutation({
      query: ({ id, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/excavation_permit_questionnaire_key/${id}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
    }),
    //end
    updatePipeJackingQuestionnaireKeyById: builder.mutation({
      query: ({ id, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/pipe_jacking_permit_questionnaire_key/${id}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
    }),
    //end
    updatePilingQuestionnaireKeyById: builder.mutation({
      query: ({ id, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/piling_permit_questionnaire_key/${id}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
    }),
    //end
    deleteQuestionnaireHotworkPermit: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/hotwork_permit_questionnaire_key/${id}/`,
        method: "DELETE",
      }),
    }),
    //end
    deleteQuestionnaireConfinedSpacePermit: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/confined_space_permit_questionnaire_key/${id}/`,
        method: "DELETE",
      }),
    }),
    //end
    deleteQuestionnaireLiftingWorkPermit: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/lifting_permit_questionnaire_key/${id}/`,
        method: "DELETE",
      }),
    }),
    //end
    deleteQuestionnaireWorkAtHeightPermit: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/workatheight_permit_questionnaire_key/${id}/`,
        method: "DELETE",
      }),
    }),
    //end
    deleteQuestionnaireGeneralWorkPermit: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/generalwork_permit_questionnaire_key/${id}/`,
        method: "DELETE",
      }),
    }),
    //end
    deleteQuestionnaireExcavationPermit: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/excavation_permit_questionnaire_key/${id}/`,
        method: "DELETE",
      }),
    }),
    //end
    deleteQuestionnairePipeJackingPermit: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/pipe_jacking_permit_questionnaire_key/${id}/`,
        method: "DELETE",
      }),
    }),
    //end
    deleteQuestionnairePilingPermit: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/piling_permit_questionnaire_key/${id}/`,
        method: "DELETE",
      }),
    }),
    //end

    // Health Declaration
    createHealthDeclaration: builder.mutation({
      query: (newMeetingData) => ({
        url: `/safety_app/api/v1/toolbox_health_declarations_view/`,
        method: "POST",
        body: newMeetingData,
      }),
      invalidatesTags: [
        { type: "ToolboxMeeting" },
        { type: "GetToolboxMeeting" },
      ],
    }),

    //----Pilling Permit----//

    getPillingPermitView: builder.query({
      query: (page) =>
        `/safety_app/api/v1/piling_permit_view/?page=${page.page}&page_size=${page.pageSize}&search=${page.search}&report_status=${page.report_status}&start_date=${page.start_date}&end_date=${page.end_date}&project=${page.project}`,
      providesTags: ["GetPillingPermit"],
    }),
    getPillingPermitByNumber: builder.query({
      query: (permit_no) =>
        `/safety_app/api/v1/piling_permit_view/${permit_no}/`,
      providesTags: (result, error, permit_no) => [
        {
          type: "GetPillingPermitByNumber",
          id: permit_no,
        },
      ],
    }),
    createPillingPermit: builder.mutation({
      query: (newPermitData) => ({
        url: "/safety_app/api/v1/piling_permit_create_update/",
        method: "POST",
        body: newPermitData,
      }),
      invalidatesTags: ["GetPillingPermit"],
    }),
    deletePillingPermit: builder.mutation({
      query: ({ permitNo, permitData }) => ({
        url: `/safety_app/api/v1/piling_permit_create_update/${permitNo}/?user_comments=${permitData.user_comments}&latitude=${permitData.latitude}&longitude=${permitData.longitude}`,
        method: "DELETE",
      }),
    }),
    updatePillingPermit: builder.mutation({
      query: ({ permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/piling_permit_create_update/${permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
      invalidatesTags: (result, error, { permit_no }) => [
        { type: "GetPillingPermitByNumber", id: permit_no },
        { type: "GetPillingPermit" },
      ],
    }),
    uploadPillingPermitDocument: builder.mutation({
      query: ({ permit_no, documentData }) => ({
        url: `/safety_app/api/v1/piling_permit_document_update/${permit_no}/`,
        method: "PATCH",
        body: documentData,
      }),
    }),
    deletePillingPermitDocument: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/piling_permit_document_delete/${id}/`,
        method: "DELETE",
      }),
    }),
    updatePillingWorkAreaAllocation: builder.mutation({
      query: ({ permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/piling_area_allocation/${permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
    }),
    deletePillingWorkAreaAllocation: builder.mutation({
      query: ({
        sectionId,
        employeeId,
        type,
      }: {
        sectionId: number;
        employeeId: number | null;
        type: "employee" | "section";
      }) => {
        return {
          url: `/safety_app/api/v1/piling_area_allocation_delete/${sectionId}/`, // Updated endpoint
          method: "PATCH",
          body: {
            type, // Pass type in the body
            ...(type === "employee" && { employee_id: employeeId }), // Conditionally include employee_id
          },
        };
      },
    }),
    getPillingPermitQuestionnaireKeys: builder.query({
      query: () => "/safety_app/api/v1/piling_permit_questionnaire_key/",
    }),
    getPillingPermitQuestionnaireKeyById: builder.query({
      query: (id) =>
        `/safety_app/api/v1/piling_permit_questionnaire_key/${id}/`,
    }),
    createPillingQuestionnaireKey: builder.mutation({
      query: (newQuestionnaireKeyData) => ({
        url: "/safety_app/api/v1/piling_permit_questionnaire_key/",
        method: "POST",
        body: newQuestionnaireKeyData,
      }),
    }),
    updatePillingPermitQuestionnaireByPermitNo: builder.mutation({
      query: ({ permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/piling_permit_questionnaire_update/${permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { cs_permit_no }) => [
      //   { type: "GetConfinedSpacePermitByNumber", id: cs_permit_no },
      // ],
    }),

    //----Pipe Jacking Permit----//

    getPipeJackingPermitView: builder.query({
      query: (page) =>
        `/safety_app/api/v1/pipe_jacking_permit_view/?page=${page.page}&page_size=${page.pageSize}&search=${page.search}&report_status=${page.report_status}&start_date=${page.start_date}&end_date=${page.end_date}&project=${page.project}`,
      providesTags: ["GetPipeJackingPermit"],
    }),
    getPipeJackingPermitByNumber: builder.query({
      query: (permit_no) =>
        `/safety_app/api/v1/pipe_jacking_permit_view/${permit_no}/`,
      providesTags: (result, error, permit_no) => [
        {
          type: "GetPipeJackingPermitByNumber",
          id: permit_no,
        },
      ],
    }),
    createPipeJackingPermit: builder.mutation({
      query: (newPermitData) => ({
        url: "/safety_app/api/v1/pipe_jacking_permit_create_update/",
        method: "POST",
        body: newPermitData,
      }),
      invalidatesTags: ["GetPipeJackingPermit"],
    }),
    deletePipeJackingPermit: builder.mutation({
      query: ({ permitNo, permitData }) => ({
        url: `/safety_app/api/v1/pipe_jacking_permit_create_update/${permitNo}/?user_comments=${permitData.user_comments}&latitude=${permitData.latitude}&longitude=${permitData.longitude}`,
        method: "DELETE",
      }),
    }),
    updatePipeJackingPermit: builder.mutation({
      query: ({ permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/pipe_jacking_permit_create_update/${permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
      invalidatesTags: (result, error, { permit_no }) => [
        { type: "GetPipeJackingPermitByNumber", id: permit_no },
        { type: "GetPipeJackingPermit" },
      ],
    }),
    uploadPipeJackingPermitDocument: builder.mutation({
      query: ({ permit_no, documentData }) => ({
        url: `/safety_app/api/v1/pipe_jacking_permit_document_update/${permit_no}/`,
        method: "PATCH",
        body: documentData,
      }),
    }),
    deletePipeJackingPermitDocument: builder.mutation({
      query: ({ id }) => ({
        url: `/safety_app/api/v1/pipe_jacking_permit_document_delete/${id}/`,
        method: "DELETE",
      }),
    }),
    updatePipeJackingWorkAreaAllocation: builder.mutation({
      query: ({ permit_no, updatedPermitData }) => ({
        url: `/safety_app/api/v1/pipe_jacking_area_allocation/${permit_no}/`,
        method: "PATCH",
        body: updatedPermitData,
      }),
    }),
    deletePipeJackingWorkAreaAllocation: builder.mutation({
      query: ({
        sectionId,
        employeeId,
        type,
      }: {
        sectionId: number;
        employeeId: number | null;
        type: "employee" | "section";
      }) => {
        return {
          url: `/safety_app/api/v1/pipe_jacking_area_allocation_delete/${sectionId}/`, // Updated endpoint
          method: "PATCH",
          body: {
            type, // Pass type in the body
            ...(type === "employee" && { employee_id: employeeId }), // Conditionally include employee_id
          },
        };
      },
    }),
    getPipeJackingPermitQuestionnaireKeys: builder.query({
      query: () => "/safety_app/api/v1/pipe_jacking_permit_questionnaire_key/",
    }),
    getPipeJackingPermitQuestionnaireKeyById: builder.query({
      query: (id) =>
        `/safety_app/api/v1/pipe_jacking_permit_questionnaire_key/${id}/`,
    }),
    createPipeJackingQuestionnaireKey: builder.mutation({
      query: (newQuestionnaireKeyData) => ({
        url: "/safety_app/api/v1/pipe_jacking_permit_questionnaire_key/",
        method: "POST",
        body: newQuestionnaireKeyData,
      }),
    }),
    updatePipeJackingPermitQuestionnaireByPermitNo: builder.mutation({
      query: ({ permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/pipe_jacking_permit_questionnaire_update/${permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { cs_permit_no }) => [
      //   { type: "GetConfinedSpacePermitByNumber", id: cs_permit_no },
      // ],
    }),


    //--- SafetyApp Dynamic Questionnaire EndPoints
    getEquipmentUsedView: builder.query({
      query: () => `/equipment_used/api/v1/equipment_used/?is_has_questions=${true}`,
      // providesTags: ["GetHomeWorkPermit"],
    }),

    updateHotworkDynamicQuestionnaireByPermitNo: builder.mutation({
      query: ({ hw_permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/hotwork_permit_equipment_used_questionnaire_update/${hw_permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { hw_permit_no }) => [
      //   { type: "GetHotworkPermitByNumber", id: hw_permit_no },
      // ],
    }),

    updateGeneralworkDynamicQuestionnaireByPermitNo: builder.mutation({
      query: ({ gw_permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/generalwork_permit_equipment_used_questionnaire_update/${gw_permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { gw_permit_no }) => [
      //   { type: "GetGeneralworkPermitByNumber", id: gw_permit_no },
      // ],
    }),

    updateConfinedSpaceDynamicQuestionnaireByPermitNo: builder.mutation({
      query: ({ cs_permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/confinedspace_permit_equipment_used_questionnaire_update/${cs_permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { cs_permit_no }) => [
      //   { type: "GetConfinedSpacePermitByNumber", id: cs_permit_no },
      // ],
    }),

    updateLiftingWorkDynamicQuestionnaireByPermitNo: builder.mutation({
      query: ({ lp_permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/lifting_permit_equipment_used_questionnaire_update/${lp_permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { lp_permit_no }) => [
      //   { type: "GetLiftingWorkPermitByNumber", id: lp_permit_no },
      // ],
    }),

    updateWorkAtHeightDynamicQuestionnaireByPermitNo: builder.mutation({
      query: ({ wh_permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/workatheight_permit_equipment_used_questionnaire_update/${wh_permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      invalidatesTags: (result, error, { wh_permit_no }) => [
        { type: "GetWorkAtHeightPermitByNumber", id: wh_permit_no },
      ],
    }),

    updateExcavationDynamicQuestionnaireByPermitNo: builder.mutation({
      query: ({ ew_permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/generalwork_permit_equipment_used_questionnaire_update/${ew_permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { ew_permit_no }) => [
      //   { type: "GetExcavationPermitByNumber", id: ew_permit_no },
      // ],
    }),
    updatePipeJackingDynamicQuestionnaireByPermitNo: builder.mutation({
      query: ({ permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/pipejacking_permit_equipment_used_questionnaire_update/${permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { ew_permit_no }) => [
      //   { type: "GetExcavationPermitByNumber", id: ew_permit_no },
      // ],
    }),
    updatePilingPermitDynamicQuestionnaireByPermitNo: builder.mutation({
      query: ({ permit_no, updatedQuestionnaireKeyData }) => ({
        url: `/safety_app/api/v1/piling_permit_questionnaire_update/${permit_no}/`,
        method: "PATCH",
        body: updatedQuestionnaireKeyData,
      }),
      // invalidatesTags: (result, error, { ew_permit_no }) => [
      //   { type: "GetExcavationPermitByNumber", id: ew_permit_no },
      // ],
    }),
    getGeneralDynamicQuestionsByNumber: builder.query({
      query: (gw_permit_no) =>
        `/safety_app/api/v1/generalwork_permit_equipment_used_questionnaire_update/${gw_permit_no}/`,
      providesTags: (result, error, gw_permit_no) => [
        {
          type: "GetGeneralworkPermitByNumber",
          id: gw_permit_no,
        },
      ],
    }),

    getHotWorkDynamicQuestionsByNumber: builder.query({
      query: (hw_permit_no) =>
        `/safety_app/api/v1/hotwork_permit_equipment_used_questionnaire_update/${hw_permit_no}/`,
      providesTags: (result, error, hw_permit_no) => [
        {
          type: "GetHotworkPermitByNumber",
          id: hw_permit_no,
        },
      ],
    }),

    getDynamicQuestionsByIds: builder.query({
      query: (equipmentIds) =>
        `/equipment_used/api/v1/equipment_used_questionnaire_key/?equipment_used_list=${equipmentIds}`,
      // providesTags: ["GetToolboxMeeting"],
    }),

    //PDF DOWNLOADS

    getAccidentReportDownloadUids: builder.mutation({
      query: (report_no) => ({
        url: `/generator/api/v1/generate-accident-report-pdf/${report_no}`,
        method: 'GET',
      }),
    }),

    getNearMissReportDownloadUids: builder.mutation({
      query: (report_no) => ({
        url: `/generator/api/v1/generate-near-miss-report-pdf/${report_no}`,
        method: 'GET',
      }),
    }),

    getInspectionDownloadUids: builder.mutation({
      query: (report_no) => ({
        url: `/generator/api/v1/generate-inspection-pdf/${report_no}`,
        method: 'GET',
      }),
    }),


    getRiskAssesmentDownloadUids: builder.mutation({
      query: (report_no) => ({
        url: `/generator/api/v1/generate-risk-assessment-pdf/${report_no}`,
        method: 'GET',
      }),
    }),

    getPermitDownloadUids: builder.mutation({
      query: (report_no) => ({
        url: `/generator/api/v1/generate-pdf-async/${report_no}`,
        method: 'GET',
      }),
    }),

    generateReportsPDFByUuid: builder.mutation({
      query: ({ uuid, permitNo }) => ({
        url: `/generator/api/v1/generate-pdf-signed-url/${uuid}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  //Home Work Permit
  useGetHomeworkPermitViewQuery,
  useGetHomeworkPermitByNumberQuery,
  useCreateHomeworkPermitMutation,
  useUpdateHomeworkPermitMutation,
  useGetHomeworkPermitQuestionnaireKeysQuery,
  useGetHomeworkPermitQuestionnaireKeyByIdQuery,
  useCreateHomeworkPermitQuestionnaireKeyMutation,
  useUpdateHomeworkPermitQuestionnaireKeyByHwPermitNoMutation,
  useUpdateHomeworkPermitToolboxMeetingByHwPermitNoMutation,
  useGenerateHomeworkPermitPDFMutation,
  useUploadHomeworkPermitDocumentMutation,
  useDeleteHomeworkPermitDocumentMutation,
  useUpdateHomeworkWorkAreaAllocationMutation,
  useDeleteHomeworkWorkAreaAllocationMutation,

  //Hot Work Permit
  useGetHotworkPermitViewQuery,
  useGetHotworkPermitByNumberQuery,
  useCreateHotworkPermitMutation,
  useDeleteHotworkPermitMutation,
  useUpdateHotworkPermitMutation,
  useUploadHotworkPermitDocumentMutation,
  useDeleteHotworkPermitDocumentMutation,
  useUpdateHotworkWorkAreaAllocationMutation,
  useDeleteHotworkWorkAreaAllocationMutation,
  useGetHotworkQuestionnaireKeysQuery,
  useGetHotworkQuestionnaireKeyByIdQuery,
  useCreateHotworkQuestionnaireKeyMutation,
  useUpdateHotworkQuestionnaireKeyByIdMutation,
  useUpdateHotworkQuestionnaireByPermitNoMutation,

  // Generalwork Permit Hooks
  useGetGeneralworkPermitViewQuery,
  useGetGeneralworkPermitByNumberQuery,
  useCreateGeneralworkPermitMutation,
  useUpdateGeneralworkPermitMutation,
  useDeleteGeneralworkPermitMutation,
  useUploadGeneralworkPermitDocumentMutation,
  useDeleteGeneralworkPermitDocumentMutation,
  useUpdateGeneralworkWorkAreaAllocationMutation,
  useDeleteGeneralworkWorkAreaAllocationMutation,
  useGetGeneralworkQuestionnaireKeysQuery,
  useGetGeneralworkQuestionnaireKeyByIdQuery,
  useCreateGeneralworkQuestionnaireKeyMutation,
  useUpdateGeneralworkQuestionnaireByPermitNoMutation,

  //WorkAtHeight Permit Hooks
  useGetWorkAtHeightPermitViewQuery,
  useGetWorkAtHeightPermitByNumberQuery,
  useCreateWorkAtHeightPermitMutation,
  useUpdateWorkAtHeightPermitMutation,
  useDeleteWorkAtHeightPermitMutation,
  useUploadWorkAtHeightPermitDocumentMutation,
  useDeleteWorkAtHeightPermitDocumentMutation,
  useUpdateWorkAtHeightWorkAreaAllocationMutation,
  useDeleteWorkAtHeightWorkAreaAllocationMutation,
  useGetWorkAtHeightQuestionnaireKeysQuery,
  useGetWorkAtHeightQuestionnaireKeyByIdQuery,
  useCreateWorkAtHeightQuestionnaireKeyMutation,
  useUpdateWorkAtHeightQuestionnaireByPermitNoMutation,

  // Excavation Permit Hooks
  useGetExcavationPermitViewQuery,
  useGetExcavationPermitByNumberQuery,
  useCreateExcavationPermitMutation,
  useUpdateExcavationPermitMutation,
  useDeleteExcavationPermitMutation,
  useUploadExcavationPermitDocumentMutation,
  useDeleteExcavationPermitDocumentMutation,
  useUpdateExcavationWorkAreaAllocationMutation,
  useDeleteExcavationWorkAreaAllocationMutation,
  useGetExcavationQuestionnaireKeysQuery,
  useGetExcavationQuestionnaireKeyByIdQuery,
  useCreateExcavationQuestionnaireKeyMutation,
  useUpdateExcavationQuestionnaireByPermitNoMutation,

  //Lifting Work Permit
  useGetLiftingWorkPermitViewQuery,
  useGetLiftingWorkPermitByNumberQuery,
  useCreateLiftingWorkPermitMutation,
  useUpdateLiftingWorkPermitMutation,
  useDeleteLiftingWorkPermitMutation,
  useUploadLiftingWorkPermitDocumentMutation,
  useDeleteLiftingWorkPermitDocumentMutation,
  useUpdateLiftingWorkAreaAllocationMutation,
  useDeleteLiftingWorkAreaAllocationMutation,
  useGetLiftingWorkQuestionnaireKeysQuery,
  useGetLiftingWorkQuestionnaireKeyByIdQuery,
  useCreateLiftingWorkQuestionnaireKeyMutation,
  useUpdateLiftingWorkQuestionnaireByPermitNoMutation,

  //Confined Space Permit
  useGetConfinedSpacePermitViewQuery,
  useGetConfinedSpacePermitByNumberQuery,
  useCreateConfinedSpacePermitMutation,
  useUpdateConfinedSpacePermitMutation,
  useDeleteConfinedSpacePermitMutation,
  useUploadConfinedSpacePermitDocumentMutation,
  useDeleteConfinedSpacePermitDocumentMutation,
  useUpdateConfinedSpaceWorkAreaAllocationMutation,
  useDeleteConfinedSpaceWorkAreaAllocationMutation,
  useGetConfinedSpaceQuestionnaireKeysQuery,
  useGetConfinedSpaceQuestionnaireKeyByIdQuery,
  useCreateConfinedSpaceQuestionnaireKeyMutation,
  useUpdateConfinedSpaceQuestionnaireByPermitNoMutation,

  // Toolbox Meeting
  useGetToolboxMeetingViewQuery,
  useGetToolboxMeetingByFilterQuery,
  useLazyGetToolboxMeetingByFilterQuery,
  useGetToolboxMeetingByIdQuery,
  useCreateToolboxMeetingMutation,
  useUpdateToolboxMeetingMutation,
  useUpdateToolboxAttendanceMutation,
  useLinkToolboxMeetingMutation,
  useUploadToolboxDocumentMutation,
  useDeleteToolboxDocumentMutation,
  useDeleteToolboxAttendanceMutation,

  //Questionnaireupdate
  useUpdateGeneralWorkQuestionnaireKeyByIdMutation,
  useUpdateLiftingWorkQuestionnaireKeyByIdMutation,
  useUpdateConfinedSpaceQuestionnaireKeyByIdMutation,
  useUpdateWorkAtHeightQuestionnaireKeyByIdMutation,
  useUpdateExcavationQuestionnaireKeyByIdMutation,
  useUpdatePilingQuestionnaireKeyByIdMutation,
  useUpdatePipeJackingQuestionnaireKeyByIdMutation,

  //Questionnairedelete
  useDeleteQuestionnaireHotworkPermitMutation,
  useDeleteQuestionnaireConfinedSpacePermitMutation,
  useDeleteQuestionnaireLiftingWorkPermitMutation,
  useDeleteQuestionnaireWorkAtHeightPermitMutation,
  useDeleteQuestionnaireGeneralWorkPermitMutation,
  useDeleteQuestionnaireExcavationPermitMutation,
  useCreateHealthDeclarationMutation,
  useDeleteQuestionnairePilingPermitMutation,
  useDeleteQuestionnairePipeJackingPermitMutation,

  //Pilling Permit
  useGetPillingPermitViewQuery,
  useGetPillingPermitByNumberQuery,
  useCreatePillingPermitMutation,
  useUpdatePillingPermitMutation,
  useDeletePillingPermitMutation,
  useUploadPillingPermitDocumentMutation,
  useDeletePillingPermitDocumentMutation,
  useCreatePillingQuestionnaireKeyMutation,
  useDeletePillingWorkAreaAllocationMutation,
  useUpdatePillingWorkAreaAllocationMutation,
  useGetPillingPermitQuestionnaireKeyByIdQuery,
  useGetPillingPermitQuestionnaireKeysQuery,
  useUpdatePillingPermitQuestionnaireByPermitNoMutation,

  //PipeJacking Permit
  useCreatePipeJackingPermitMutation,
  useCreatePipeJackingQuestionnaireKeyMutation,
  useGetPipeJackingPermitByNumberQuery,
  useGetPipeJackingPermitViewQuery,
  useDeletePipeJackingPermitDocumentMutation,
  useDeletePipeJackingPermitMutation,
  useDeletePipeJackingWorkAreaAllocationMutation,
  useGetPipeJackingPermitQuestionnaireKeyByIdQuery,
  useGetPipeJackingPermitQuestionnaireKeysQuery,
  useUpdatePipeJackingPermitMutation,
  useUpdatePipeJackingPermitQuestionnaireByPermitNoMutation,
  useUpdatePipeJackingWorkAreaAllocationMutation,
  useUploadPipeJackingPermitDocumentMutation,

  useGetDynamicQuestionsByIdsQuery,
  useGetEquipmentUsedViewQuery,
  useUpdateConfinedSpaceDynamicQuestionnaireByPermitNoMutation,
  useUpdateLiftingWorkDynamicQuestionnaireByPermitNoMutation,
  useUpdateExcavationDynamicQuestionnaireByPermitNoMutation,
  useUpdateGeneralworkDynamicQuestionnaireByPermitNoMutation,
  useUpdateWorkAtHeightDynamicQuestionnaireByPermitNoMutation,
  useUpdateHotworkDynamicQuestionnaireByPermitNoMutation,
  useUpdatePilingPermitDynamicQuestionnaireByPermitNoMutation,
  useUpdatePipeJackingDynamicQuestionnaireByPermitNoMutation,

  useGetAccidentReportDownloadUidsMutation,
  useGetNearMissReportDownloadUidsMutation,
  useGetInspectionDownloadUidsMutation,
  useGetRiskAssesmentDownloadUidsMutation,
  useGetPermitDownloadUidsMutation,
  useGenerateReportsPDFByUuidMutation

} = safetyAppSlice;
